import { DeleteOutline } from "@mui/icons-material";
import { alpha, Avatar, Box, IconButton, Typography, useTheme } from "@mui/material";
import { font } from "../../styles/font";
import { blue, grey, red, yellow } from "@mui/material/colors";
import { useState } from "react";
import { TaskCustomIcon } from "../styled/CustomIcons";
import CreatedByAvatar from "../common/CreatedByAvatar";
import { KeyboardKeys } from "../../constant/KeyboardKeys";
import ShortcutText from "../common/ShortcutText";
import { LightTooltip } from "../styled/CustomComponent";
import { useTranslation } from "react-i18next";
import { Entity } from "../../constant/ApplicationVariables";
import { CARD_COLOR } from "../../constant/ApplicationJson";
import Badge from "../common/Badge";

const Priority = {
    low: "Low",
    medium: "Medium",
    high: "High",
};

const PriorityColor = {
    [Priority.low]: yellow,
    [Priority.medium]: blue,
    [Priority.high]: red,
}

function GetPriorityColor(text, colorStrength) {
    const colStrength = colorStrength || 50;
    const trimmedText = text?.trim() || Priority.medium;
    
    return PriorityColor[trimmedText]?.[colStrength] || blue[colStrength];
}

function getUserColor(username) {
    if (!username) return '#cccccc';
    
    let hash = 0;
    for (let i = 0; i < username.length; i++) {
        hash = username.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 255;
        color += ('00' + value.toString(16)).substr(-2);
    }

    return color;
}

export default function TaskCard({ 
    item, 
    handleClick, 
    handleEditClick, 
    handleClickDelete,
    isLoading = false 
}) {
    const theme = useTheme();
    const { t } = useTranslation('translation', { keyPrefix: 'project' });

    const cardColor = CARD_COLOR[Entity.task] || theme.palette.primary.main;

    if (isLoading) {
        return (
            <Box 
                className="flex flex-col justify-between w-full h-full gap-4 px-4 py-4 animate-pulse"
                borderRadius={theme.shape.borderRadius}
                bgcolor={theme.palette.background.paper}
                border={`1px solid ${theme.palette.divider}`}
            >
                <Box className="flex items-center gap-2 px-0">
                    <Box 
                        sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: theme.shape.borderRadius,
                            background: theme.palette.action.hover
                        }}
                    />
                    <Box className="flex-1 flex justify-end">
                        <Box 
                            sx={{ 
                                width: 32, 
                                height: 32, 
                                borderRadius: "50%",
                                background: theme.palette.action.hover
                            }}
                        />
                    </Box>
                </Box>
                
                <Box className="flex-1 flex flex-col gap-4">
                    <Box className="flex flex-col gap-2">
                        <Box 
                            sx={{ 
                                height: 20, 
                                width: "70%", 
                                borderRadius: 1,
                                background: theme.palette.action.hover
                            }}
                        />
                        <Box 
                            sx={{ 
                                height: 16, 
                                width: "90%", 
                                borderRadius: 1,
                                background: theme.palette.action.hover
                            }}
                        />
                        <Box 
                            sx={{ 
                                height: 16, 
                                width: "80%", 
                                borderRadius: 1,
                                background: theme.palette.action.hover
                            }}
                        />
                    </Box>
                    
                    <Box className="flex items-center gap-2">
                        <Box 
                            sx={{ 
                                width: 80, 
                                height: 24, 
                                borderRadius: 12,
                                background: theme.palette.action.hover
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        );
    }

    if (!item) {
        return null; // Or return a placeholder/empty state
    }

    return (
        <Box 
            className="flex flex-col justify-between w-full h-full gap-4 px-4 py-4" 
            title={`${item?.title || ''} - ${item?.description || ''}`.trim()}
            border={`1px solid ${theme.palette.divider}`} 
            borderRadius={theme.shape.borderRadius}
            bgcolor={theme.palette.background.paper}
            sx={{
                transition: 'all 0.2s ease',
                '&:hover': {
                    boxShadow: theme.shadows[2],
                    borderColor: theme.palette.primary.main,
                    transform: 'translateY(-2px)',
                    cursor: 'pointer'
                }
            }}
        >
            {/* upper container */}
            <Box className="flex items-center gap-2 px-0">
                <Avatar 
                    variant="rounded" 
                    sx={{ 
                        width: 40,
                        height: 40,
                        background: theme.palette.primary.light, 
                        color: theme.palette.primary.main 
                    }}
                >
                    <TaskCustomIcon sx={{ fontSize: font.size.fs_3, color: "inherit" }} />
                </Avatar>

                <Box className="flex-1 flex items-center justify-end gap-2">
                    {handleClickDelete && (
                        <LightTooltip
                            title={
                                <Box className="flex items-center gap-1">
                                    <Typography fontSize={font.size.fs_5} color="textPrimary">
                                        {t("deleteText") || "Delete"}
                                    </Typography>
                                    <ShortcutText keyboardShortcutText={KeyboardKeys.Delete} textColor="textSecondary" />
                                </Box>
                            }
                        >
                            <IconButton 
                                color="error" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClickDelete();
                                }}
                                sx={{ 
                                    '&:hover': {
                                        backgroundColor: theme.palette.error.light,
                                    }
                                }}
                            >
                                <DeleteOutline />
                            </IconButton>
                        </LightTooltip>
                    )}
                </Box>
            </Box>

            {/* main container */}
            <Box 
                className="flex-1 flex flex-col gap-4"
                onClick={() => handleClick && handleClick(item)}
                sx={{
                    cursor: handleClick ? 'pointer' : 'default'
                }}
            >
                <Box className="flex-1 flex flex-col gap-2">
                    <Typography 
                        fontSize={font.size.fs_4} 
                        color={theme.palette.text.primary} 
                        fontWeight={600}
                        className="line-clamp-1"
                    >
                        {item?.title || "Untitled Task"}
                    </Typography>
                    <Typography 
                        fontSize={font.size.fs_5} 
                        color={theme.palette.text.secondary} 
                        className="line-clamp-2"
                        sx={{
                            minHeight: '2.5em' // Ensure consistent height for 2 lines
                        }}
                    >
                        {item?.description || "No description provided"}
                    </Typography>
                </Box>

                {/* Footer with status and creator */}
                <Box className="w-full flex items-center justify-between">
                    <Box className="flex items-center gap-2">
                        {item?.status && (
                            <Badge text={item.status} />
                        )}
                    </Box>
                    
                    {item?.createdBy && (
                        <CreatedByAvatar data={item} />
                    )}
                </Box>
            </Box>
        </Box>
    )
}

// Optional: Export a variant with priority display
export function TaskCardWithPriority({ item, ...props }) {
    const theme = useTheme();
    
    return (
        <Box
            sx={{
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 4,
                    height: '100%',
                    backgroundColor: item?.priority ? GetPriorityColor(item.priority, 500) : theme.palette.primary.main,
                    borderTopLeftRadius: theme.shape.borderRadius,
                    borderBottomLeftRadius: theme.shape.borderRadius
                }
            }}
        >
            <TaskCard item={item} {...props} />
            {item?.priority && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        px: 1,
                        py: 0.5,
                        backgroundColor: GetPriorityColor(item.priority, 100),
                        color: GetPriorityColor(item.priority, 800),
                        borderRadius: 1,
                        fontSize: font.size.fs_6,
                        fontWeight: 600,
                        zIndex: 1
                    }}
                >
                    {item.priority}
                </Box>
            )}
        </Box>
    );
}

// Optional: Export a variant with assigned users
export function TaskCardWithAssigned({ item, ...props }) {
    const theme = useTheme(); // Move useTheme() to the top of the component
    
    return (
        <Box className="flex flex-col gap-2">
            <TaskCard item={item} {...props} />
            {item?.assignedTo && item.assignedTo.length > 0 && (
                <Box className="flex items-center gap-1 pl-4">
                    {item.assignedTo.map((user, index) => (
                        <Avatar 
                            key={user?.id || index}
                            alt={user?.name} 
                            src={user?.image} 
                            sx={{ 
                                width: 24, 
                                height: 24, 
                                background: getUserColor(user?.name),
                                border: `2px solid ${theme.palette.background.paper}`, // Use theme from above
                                fontSize: font.size.fs_7
                            }} 
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}